import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Link,
  Typography,
} from "@mui/material"
import { ReactNode } from "react"

type Question = {
  question: ReactNode
  answer: ReactNode
}

type Category = {
  name: ReactNode
  questions: Question[]
}

const categories: Category[] = [
  {
    name: <>1. General Questions</>,
    questions: [
      {
        question: <>What is Narvan?</>,
        answer: (
          <>
            Narvan is a <strong>smart plant care app</strong> that helps users
            manage their plants by providing{" "}
            <strong>reminders for watering, feeding, and general care</strong>.
            The app works by scanning a <strong>QR code</strong> on a Narvan
            plant to track its health and progress.
          </>
        ),
      },
      {
        question: <>How does the Narvan app work</>,
        answer: (
          <ul>
            <li>
              <strong>Buy a Narvan Plant</strong> - Purchase a plant from an
              official retailer.
            </li>
            <li>
              <strong>Download &amp; Install the App</strong> - Available on the
              App Store (iOS) and Google Play (Android).
            </li>
            <li>
              <strong>Create an Account or Log In</strong> - Set up your profile
              with an email and password.
            </li>
            <li>
              <strong>Scan Your Plant's QR Code</strong> - Add the plant to your
              profile.
            </li>
            <li>
              <strong>Set Up Your Plant</strong> - Name your plant, assign it a
              location, and follow care instructions.
            </li>
            <li>
              <strong>Track Plant Care Tasks</strong> - Complete watering, and
              soil check jobs.
            </li>
            <li>
              <strong>Monitor Plant Health</strong> - Use the plant status icons
              to know when care is needed.
            </li>
          </ul>
        ),
      },
      {
        question: <>The app isn't working properly. What should I do?</>,
        answer: (
          <>
            Try the following steps:
            <ul>
              <li>Ensure you have the latest version of the app installed.</li>
              <li>Restart your device.</li>
              <li>Reinstall the app.</li>
            </ul>
          </>
        ),
      },
      {
        question: <>How do I report a bug or suggest a feature?</>,
        answer: (
          <>
            Please email us at{" "}
            <Link href="mailto:support@narvan.co.uk">support@narvan.co.uk</Link>{" "}
            with details.
          </>
        ),
      },
    ],
  },
  {
    name: <>2. Account &amp; Login</>,
    questions: [
      {
        question: <>How do I create a Narvan account?</>,
        answer: (
          <ul>
            <li>Open the Narvan app.</li>
            <li>
              Tap <strong>Sign Up</strong>.
            </li>
            <li>
              Enter your <strong>email address</strong> and create a{" "}
              <strong>
                secure password (at least 8 characters, including 1 symbol and 1
                number)
              </strong>
              .
            </li>
            <li>
              Tap <strong>Create Account</strong>.
            </li>
            <li>Log in and start adding plants!</li>
          </ul>
        ),
      },
      {
        question: <>What should I do if I forget my password?</>,
        answer: (
          <ul>
            <li>
              Tap <strong>Forgot Password?</strong> on the login screen.
            </li>
            <li>
              Enter your <strong>registered email address</strong>.
            </li>
            <li>
              You will receive a <strong>verification code</strong> via email.
            </li>
            <li>
              Enter the <strong>verification code</strong> in the app.
            </li>
            <li>
              Set a new password (must be at least <strong>8 characters</strong>
              , with <strong>1 symbol</strong> and <strong>1 number</strong>).
            </li>
            <li>
              Make sure you use the <strong>same email</strong> that was
              registered with your account.
            </li>
          </ul>
        ),
      },
    ],
  },
  {
    name: <>3. Adding &amp; Managing Plants</>,
    questions: [
      {
        question: <>What do I see when I log in?</>,
        answer: (
          <>
            After logging in, you will land on the <strong>My Plants</strong>{" "}
            screen, which is the <strong>home screen</strong> of the app. If you
            haven't added a plant yet, the screen will be empty, showing only a
            '+' (Add) button. You can also navigate using the bottom navigation
            bar, which includes:
            <ul>
              <li>
                <strong>My Plants</strong> - Displays the plants you have added.
              </li>
              <li>
                <strong>Jobs</strong> - Lists tasks related to plant care (empty
                if no plants are added).
              </li>
              <li>
                <strong>Shop</strong> - Shows locations where you can buy Narvan
                plants.
              </li>
            </ul>
          </>
        ),
      },
      {
        question: <>How do I add a plant to the app?</>,
        answer: (
          <>
            <ul>
              <li>Tap the "+" button on the home screen.</li>
              <li>Select "Scan QR Code".</li>
              <li>Allow camera access (if not already enabled).</li>
              <li>Scan the QR code on the plant's pot.</li>
              <li>
                Follow the setup steps:
                <ul>
                  <li>Assign a custom name (e.g., "Sunny's Aloe").</li>
                  <li>Choose a location (e.g., "Living Room" or "Balcony").</li>
                  <li>Complete first job.</li>
                </ul>
              </li>
            </ul>
            Once added, the plant appears in the My Plants section.
          </>
        ),
      },
      {
        question: <>What happens after I add a plant?</>,
        answer: (
          <ul>
            <li>The app generates a Soil Check job for the plant.</li>
            <li>You can choose to complete it immediately or later.</li>
            <li>
              After the first setup, the plant's status icon will indicate its
              condition.
            </li>
          </ul>
        ),
      },
      {
        question: <>Can I add multiple plants?</>,
        answer: (
          <>
            Yes! You can add as many plants as you want. Each plant will have
            its own profile, reminders, and status indicators.
          </>
        ),
      },
    ],
  },
  {
    name: <>4. Plant Care &amp; Job Management</>,
    questions: [
      {
        question: <>What are plant care jobs?</>,
        answer: (
          <>
            Plant care jobs help you <strong>track plant health</strong>. The
            two main jobs include:
            <ul>
              <li>Soil Check - Determines if watering is needed.</li>
              <li>Watering Task - Reminds you when it's time to water.</li>
            </ul>
          </>
        ),
      },
      {
        question: <>How do I complete a plant care job?</>,
        answer: (
          <>
            <ul>
              <li>
                Go to <strong>My Jobs</strong>.
              </li>
              <li>
                Tap on one of the cards with the <strong>active job</strong>{" "}
                (e.g., Soil Check).
              </li>
              <li>
                Choose one of the following:
                <ul>
                  <li>
                    <strong>Soil is Moist</strong> - The job is marked{" "}
                    <strong>complete</strong>, and the job card disappears from
                    the <strong>My Jobs</strong> section.
                  </li>
                  <li>
                    <strong>Soil is Dry</strong> - You need to water the plant.
                  </li>
                </ul>
                Once a job is <strong>completed</strong>, it disappears from the{" "}
                <strong>My Jobs</strong> section.
              </li>
            </ul>
          </>
        ),
      },
      {
        question: <>How do I know if my plant needs care?</>,
        answer: (
          <>
            Each plant has a <strong>status icon</strong> on its card:
            <ul>
              <li>🟢 Green - All care jobs are completed.</li>
              <li>🟠 Amber - Pending care jobs need attention.</li>
              <li>🔴 Red - The plant hasn't been watered for too long.</li>
            </ul>
          </>
        ),
      },
      {
        question: <>What happens when I finish setting up my plant?</>,
        answer: (
          <>
            After setup, you will be redirected to My Plants, where you can see:
            <ul>
              <li>The new plant.</li>
              <li>Any other previously added plants.</li>
              <li>
                Each plant's <strong>status indicator</strong>,{" "}
                <strong>name</strong>, and <strong>location</strong>.
              </li>
            </ul>
          </>
        ),
      },
      {
        question: <>What happens if I click on a plant card?</>,
        answer: (
          <>
            You are taken to the <strong>Single Plant</strong> screen, where you
            can:
            <ul>
              <li>
                View detailed plant <strong>location</strong>,{" "}
                <strong>watering</strong>, and{" "}
                <strong>feeding information</strong>.
              </li>
              <li>
                Edit the <strong>plant's name</strong> and{" "}
                <strong>location</strong> or <strong>Delete the plant</strong>{" "}
                from the <strong>More Options</strong> menu.
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    name: <>5. Customising the My Plants Page</>,
    questions: [
      {
        question: <>How do I change the plant card layout?</>,
        answer: (
          <>
            On the My Plants page, you can:
            <li>
              Switch between <strong>Vertical</strong> &amp;{" "}
              <strong>Horizontal</strong> layouts.
            </li>
            <li>
              View the <strong>current season</strong> (displayed at the top).
            </li>
          </>
        ),
      },
      {
        question: <>How do I delete a plant?</>,
        answer: (
          <>
            <li>
              Option 1: Tap the <strong>More Options</strong> button and select{" "}
              <strong>Delete Plant</strong> (in the Horizontal view)
            </li>
            <li>
              Option 2: <strong>Swipe left</strong> on a plant card to delete it
              (in the vertical view)
            </li>
          </>
        ),
      },
    ],
  },
  {
    name: <>6. Settings &amp; App Preferences</>,
    questions: [
      {
        question: <>What settings can I customize?</>,
        answer: (
          <>
            Go to <strong>Settings</strong> to access:
            <ul>
              <li>
                <strong>Log Out</strong> - Sign out of your account.
              </li>
              <li>
                <strong>View Email</strong> - See your registered email.
              </li>
              <li>
                <strong>Dark Mode</strong> - Switch between light and dark
                themes.
              </li>
              <li>
                <strong>Share App</strong> - Invite friends to use Narvan.
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    name: <>7. Troubleshooting &amp; Permissions</>,
    questions: [
      {
        question: <>Why does the app ask for camera access?</>,
        answer: (
          <>
            The <strong>camera is required</strong> to scan plant QR codes. If
            you don't allow access, you{" "}
            <strong>won't be able to add new plants</strong>.
          </>
        ),
      },
      {
        question: <>What if my QR code isn't scanning?</>,
        answer: (
          <ul>
            <li>
              Ensure your <strong>camera lens is clean</strong>.
            </li>
            <li>
              Use <strong>good lighting</strong>.
            </li>
            <li>
              Hold the phone <strong>steady and close</strong> to the QR code.
            </li>
            <li>Restart the app and try again.</li>
            <li>
              <strong>Check app permissions</strong> in your phone settings.
            </li>
          </ul>
        ),
      },
    ],
  },
  {
    name: <>8. Shopping for Narvan Plants</>,
    questions: [
      {
        question: <>Where can I buy Narvan plants?</>,
        answer: (
          <>
            You can find Narvan plants at <strong>partner stores</strong> listed
            in the <strong>Shop Tab</strong> inside the app.
          </>
        ),
      },
      {
        question: <>Can I order a Narvan plant online?</>,
        answer: (
          <>
            Currently, Narvan plants are{" "}
            <strong>only available in physical stores</strong>. Online purchases
            may be added in the future.
          </>
        ),
      },
    ],
  },
  {
    name: <>9. Support &amp; Policies</>,
    questions: [
      {
        question: <>How do I contact customer support?</>,
        answer: (
          <>
            For any issues, email{" "}
            <Link href="mailto:support@narvan.co.uk">support@narvan.co.uk</Link>
            .
          </>
        ),
      },
      {
        question: (
          <>Where can I find Narvan's Privacy Policy and Terms of Use?</>
        ),
        answer: (
          <>
            Visit our official pages:
            <ul>
              <li>
                Privacy Policy:{" "}
                <Link href="https://app.narvan.co.uk/privacy">
                  https://app.narvan.co.uk/privacy
                </Link>
              </li>
              <li>
                Terms of Use:{" "}
                <Link href="https://app.narvan.co.uk/terms">
                  https://app.narvan.co.uk/terms
                </Link>
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
]

function Support() {
  return (
    <Container style={{ marginBottom: 50, paddingBottom: 100 }}>
      <Typography variant="h4" mt={4} mb={2}>
        Support for Narvan{" "}
      </Typography>
      <Typography variant="body2" mb={2}>
        Last Updated: 24/02/2025
      </Typography>
      <Typography variant="body1" mb={1}>
        Welcome to the official support page for Narvan. If you need help, check
        out our frequently asked questions or contact our support team.
      </Typography>

      <Typography variant="h4" mt={4} mb={2}>
        Narvan App - Frequently Asked Questions (FAQs)
      </Typography>
      {categories.map((category, index_category) => {
        return (
          <>
            <Typography variant="h5" key={index_category} mt={4} mb={2}>
              {category.name}
            </Typography>
            {category.questions.map((item, index_question) => {
              return (
                <Box key={`${index_category}_${index_question}`}>
                  <Accordion
                    square={true}
                    elevation={0}
                    sx={{
                      "&:before": {
                        display: "none",
                      },
                      bgcolor: "secondary.main",
                      mb: 0.4,
                    }}
                  >
                    <AccordionSummary
                      id="panel1-header"
                      aria-controls="panel1-content"
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography>{item.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ bgcolor: "secondary.light" }}>
                      <Typography>{item.answer}</Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )
            })}
          </>
        )
      })}
    </Container>
  )
}

export default Support
